import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputField from '../../../components/atom/input';
import useMemberRegist from '../member/signup/hooks/useMemberRegist';
import ModalLayout from '../../../components/layouts/ModalLayout';
import { DialogTitleItem } from '../../../components/layouts/GModalPopup';
import { GButtonBox, GPopupTitle } from '../../../components/layouts/GLayoutBox';
import DialogContent from '@mui/material/DialogContent';
import { GbasicTable, GbasicTableWrap } from '../../../components/tables/GTable';
import { GLabelAtom } from '../../../components/atom/label';
import { ValidationBox } from '../../../components/validation/ValidationBox';
import { AlignBox } from '../../../components/organism/AlignBox';
import { GButton } from '../../../components/atom/button';
import DialogActions from '@mui/material/DialogActions';
import Save from '../../../components/asset/images/Confirm.svg';
import { updateMemberPassword } from '../../../apis/vars/member/Member';
import { useCommonModal } from '../../../hooks/useCommonModal';
import MemberResetModal from './MemberResetModal';
import { useNavigate } from 'react-router-dom';

type ReIssuePasswordProps = {
  getHistoryData?: string[];
  initData?: any;
  open: boolean;
  close: () => void;
  save: () => void;
};

const ReIssuePasswordModal = ({ getHistoryData, initData, open, close }: ReIssuePasswordProps) => {
  const { t } = useTranslation();
  const t2 = t as (msgId: string, defVal: string) => string;

  const navigate = useNavigate();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [touched, setTouched] = useState({ pw1: false, pw2: false });
  const [validationErrMap, setValidationErrMap] = useState<Map<string, string>>(new Map());
  const { ovRegSchema } = useMemberRegist();
  const { openCommonModal } = useCommonModal();

  const pw1Err = touched.pw1 ? validationErrMap.get('pwd') : null;
  const pw2Err = touched.pw2
    ? !pw2
      ? t('com.label.pwd.비밀번호 확인을 입력해주세요.', '__비밀번호 확인을 입력해주세요.')
      : pw1 !== pw2
      ? t('com.label.00656', '__패스워드가 일치하지 않습니다.')
      : null
    : null;

  const checkValidate = async (schema: any, targetData: any) => {
    try {
      setValidationErrMap(new Map());
      await schema.validate(targetData, { abortEarly: false });
      return true;
    } catch (e: any) {
      const errMap = new Map();
      e.inner?.map((err) => {
        errMap.set(err.path, err.message);
      });
      setValidationErrMap(errMap);
      return;
    }
  };

  const handleOnChange = async (e) => {
    const { id, value } = e.target;

    if (id === 'pwd1') {
      setPw1(value);
      await checkValidate(ovRegSchema, { pwd: value, userId: initData?.userId ?? null });
    }
    if (id === 'pwd2') setPw2(value);
  };

  const handleOnSave = async () => {
    await checkValidate(ovRegSchema, { pwd: pw1, userId: initData?.userId ?? null });
    setTouched((t) => ({ pw1: true, pw2: true }));

    if (!(pw1 && pw2) || pw1Err || pw2Err) return;

    const newPasswordVo = {
      ...initData,
      password: pw2,
    };

    const res = await updateMemberPassword(newPasswordVo);

    if (res.successOrNot === 'Y') {
      openCommonModal({
        content: t(
          'com.label.pwd.패스워드 변경 완료되었습니다.',
          '__패스워드 변경 완료되었습니다.'
        ),
        yesCallback() {
          close();
          navigate(0);
        },
        noCallback() {
          close();
          navigate(0);
        },
      });
      return;
    } else {
      openCommonModal({
        content: t(
          'com.label.pwd.member5pwdhistory.패스워드 변경 시 이전에 사용했던 패스워드 5개는  사용 불가합니다.',
          '__패스워드 변경 시 이전에 사용했던 패스워드 5개는 사용 불가합니다.'
        ),
        yesCallback() {
          close();
          navigate(0);
        },
        noCallback() {
          close();
          navigate(0);
        },
      });
      return;
    }
  };
  const handleClose = () => {
    close();
  };

  return (
    <ModalLayout open={open} onClose={handleClose} mMinWidth={525} mMaxWidth={525}>
      <DialogTitleItem onClose={handleClose}>
        <GPopupTitle title2={t('com.label.pwd.newpwd', '__신규 비밀번호')} />
      </DialogTitleItem>
      <DialogContent className="popupContent">
        <GbasicTableWrap isBottomMgn={false}>
          <GbasicTable>
            <colgroup>
              <col style={{ width: '150px' }} />
              <col style={{ width: 'auto' }} />
            </colgroup>
            <tbody>
              <tr>
                <th>
                  <GLabelAtom align="left" isRequired={true}>
                    {t('com.label.pwd.패스워드', '__패스워드')}
                  </GLabelAtom>
                </th>
                <td>
                  {/*<label>새 비밀번호</label>*/}
                  <InputField
                    isPassword={true}
                    id="pwd1"
                    placeholder={t2('com.label.00536', '__패스워드를 입력하세요')}
                    status={pw1Err ? 'error' : 'default'}
                    helperText={pw1Err ?? undefined}
                    onChange={handleOnChange}
                    onBlur={() => setTouched((t) => ({ ...t, pw1: true }))}
                    onPaste={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </td>
              </tr>
              <tr>
                <th>
                  <GLabelAtom align="left" isRequired={true}>
                    {t('com.label.pwd.패스워드 확인', '__패스워드 확인')}
                  </GLabelAtom>
                </th>
                <td>
                  {/*<label>새 비밀번호 확인</label>*/}
                  <InputField
                    isPassword={true}
                    id="pwd2"
                    placeholder={t2('com.label.00537', '__패스워드를 다시 입력하세요')}
                    status={pw2Err ? 'error' : 'default'}
                    helperText={pw2Err ?? undefined}
                    onChange={handleOnChange}
                    onBlur={() => setTouched((t) => ({ ...t, pw2: true }))}
                    onPaste={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </td>
              </tr>
            </tbody>
          </GbasicTable>
        </GbasicTableWrap>
      </DialogContent>
      <DialogActions className="popupBottom">
        <GButtonBox>
          <GButton
            txt={t('common.button.확인', '__확인')}
            sizes="medium"
            onClick={handleOnSave}
            chkImg={Save}
            btnstyled="contained"
            btnColor="primary"
            hasImg={true}
            hasTxt={true}
          />
        </GButtonBox>
      </DialogActions>
    </ModalLayout>
  );
};

export default ReIssuePasswordModal;
